import React from 'react';
import { Package, DollarSign } from 'lucide-react';

interface AdminAssetsTabProps {
  assets: any[];
}

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    maintenance: 'bg-amber-100 text-amber-700',
    retired: 'bg-stone-100 text-stone-500',
    damaged: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${map[status] || 'bg-stone-100 text-stone-500'}`}>
      {status}
    </span>
  );
};

const AdminAssetsTab: React.FC<AdminAssetsTabProps> = ({ assets }) => {
  if (assets.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="text-center py-8 text-stone-400">
          <Package size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No assets recorded for this location</p>
        </div>
      </div>
    );
  }

  const totalValue = assets.reduce((sum, a) => sum + (a.currentValue || a.purchasePrice || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Package size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{assets.length}</p>
              <p className="text-xs text-stone-500">Total Assets</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <DollarSign size={18} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">${totalValue.toLocaleString()}</p>
              <p className="text-xs text-stone-500">Total Value</p>
            </div>
          </div>
        </div>
      </div>

      {/* Asset List */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <Package size={16} className="text-stone-500" />
          Assets
        </h3>
        <div className="space-y-2">
          {assets.map((asset: any, idx: number) => (
            <div key={asset.assetId || idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl border border-stone-100">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{asset.name}</p>
                <div className="flex items-center gap-3 mt-1">
                  {asset.category && <span className="text-xs text-stone-500">{asset.category}</span>}
                  {asset.serialNumber && <span className="text-xs text-stone-400 font-mono">{asset.serialNumber}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {asset.purchasePrice !== undefined && (
                  <p className="text-xs text-stone-500">Cost: ${Number(asset.purchasePrice).toLocaleString()}</p>
                )}
                {asset.currentValue !== undefined && (
                  <p className="text-sm font-semibold text-stone-800">${Number(asset.currentValue).toLocaleString()}</p>
                )}
              </div>
              {asset.status && <div className="shrink-0">{statusBadge(asset.status)}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAssetsTab;
